import type { GroupNodeDTO, NodeContextDTO } from '@/services/api.service';

// ============================================================
// Copie des grades LuckPerms d'un groupe de jeu vers un autre (NationsGlory : Iris vers Nova)
// ============================================================
// Supervision > Grades, bouton « Copier les grades vers… ». La définition de chaque grade coché
// (ses nodes : permissions avec leurs contextes et leur fin, héritages, poids, préfixe, suffixe, nom
// affiché, métas) devient identique dans le groupe cible.
//   - Jamais copié : qui a le grade. Seuls les nodes des GRADES sont lus et écrits, jamais ceux des
//     joueurs : personne ne gagne ni ne perd un grade.
//   - Jamais supprimé : un grade qui n'existe que dans la cible est signalé, pas touché.
//   - Rien à copier pour les cosmétiques, le chat et la boutique : configs des containers, communes
//     à tout le projet.
//
// Le panel orchestre la copie avec les points d'entrée du satellite de chaque groupe (liste des
// grades, nodes d'un grade, création d'un grade, ajout et retrait d'un node), un appel par écriture :
// la passerelle et le satellite gardent tous leurs contrôles (droit d'écrire les grades, affectation
// au groupe, grades protégés, RankNodeGuard). Contextes et fins ne sont lus que par un satellite qui
// le dit (`detailed`) : sans eux, une permission limitée à un serveur passerait pour globale et la
// copie l'étendrait à tous les serveurs. La copie est alors refusée.
//
// Fonctions pures et exécution à API injectée, sans réseau ni store (gradeCopy.test.ts).
// ============================================================

// ---- Nodes ----

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Contextes comparables : rognés, en minuscules (LuckPerms les compare ainsi), triés, sans doublon. */
export function normalizeContext(context: readonly NodeContextDTO[] | null | undefined): NodeContextDTO[] {
  const seen = new Set<string>();
  const out: NodeContextDTO[] = [];
  for (const c of context ?? []) {
    const key = String(c?.key ?? '').trim().toLowerCase();
    const value = String(c?.value ?? '').trim().toLowerCase();
    if (!key || !value || seen.has(`${key}=${value}`)) continue;
    seen.add(`${key}=${value}`);
    out.push({ key, value });
  }
  return out.sort((a, b) => cmp(a.key, b.key) || cmp(a.value, b.value));
}

/** Fin d'un node temporaire (secondes epoch), null pour un node permanent. */
export function expiryOf(n: GroupNodeDTO): number | null {
  return typeof n.expiry === 'number' && n.expiry > 0 ? n.expiry : null;
}

/** Node encore actif : un node temporaire dont la fin est passée ne compte plus (LuckPerms le retire). */
export function isLive(n: GroupNodeDTO, nowSec: number): boolean {
  const e = expiryOf(n);
  return e === null || e > nowSec;
}

/**
 * Case d'un node pour LuckPerms : même clé, mêmes contextes, temporaire ou permanent. Deux nodes de
 * même case ne coexistent pas (le second remplace le premier) : c'est l'unité de la comparaison. La
 * clé est comparée telle quelle : LuckPerms rend la même écriture des deux côtés, et le texte d'un
 * préfixe distingue majuscules et espaces.
 */
export function slotOf(n: GroupNodeDTO): string {
  const ctx = normalizeContext(n.context).map((c) => `${c.key}=${c.value}`).join('\u0002');
  return `${n.key}\u0001${ctx}\u0001${expiryOf(n) === null ? 'p' : 't'}`;
}

function sameNode(a: GroupNodeDTO, b: GroupNodeDTO): boolean {
  return a.value === b.value && expiryOf(a) === expiryOf(b);
}

/** Différence entre la définition d'un grade dans la source et dans la cible. */
export interface GradeDiff {
  /** Nodes de la source absents de la cible : ajoutés. */
  add: GroupNodeDTO[];
  /** Même case des deux côtés, autre valeur ou autre fin : nodes de la cible retirés, puis celui de la source ajouté. */
  change: { from: GroupNodeDTO[]; to: GroupNodeDTO }[];
  /** Nodes de la cible absents de la source : retirés. */
  remove: GroupNodeDTO[];
}

/**
 * Ce qu'il faut écrire dans la cible pour que ses nodes soient ceux de la source. Les nodes
 * temporaires déjà finis sont ignorés des deux côtés ; un node en double dans une case de la cible
 * est retiré avec elle.
 */
export function diffNodes(source: readonly GroupNodeDTO[], target: readonly GroupNodeDTO[], nowSec: number): GradeDiff {
  const wanted = new Map<string, GroupNodeDTO>();
  for (const n of source) {
    if (!isLive(n, nowSec)) continue;
    const s = slotOf(n);
    if (!wanted.has(s)) wanted.set(s, n);
  }
  const present = new Map<string, GroupNodeDTO[]>();
  for (const n of target) {
    if (!isLive(n, nowSec)) continue;
    const s = slotOf(n);
    const list = present.get(s);
    if (list) list.push(n);
    else present.set(s, [n]);
  }
  const diff: GradeDiff = { add: [], change: [], remove: [] };
  for (const [s, n] of wanted) {
    const there = present.get(s);
    if (!there) diff.add.push(n);
    else if (!(there.length === 1 && sameNode(there[0]!, n))) diff.change.push({ from: there, to: n });
  }
  for (const [s, there] of present) {
    if (!wanted.has(s)) diff.remove.push(...there);
  }
  return diff;
}

export function diffSize(d: GradeDiff): number {
  return d.add.length + d.change.length + d.remove.length;
}

/** Une écriture de la copie. */
export interface CopyOp {
  kind: 'add' | 'remove';
  node: GroupNodeDTO;
}

/**
 * Écritures d'une différence, dans l'ordre : ajouts, puis remplacements (retrait du node de la
 * cible, puis ajout de celui de la source), puis retraits. Ajouter avant de retirer : en cours de
 * route, personne ne perd une permission que les deux définitions donnent. Jamais d'ajout suivi du
 * retrait de la même case : LuckPerms retire une case sans regarder la valeur, ce retrait effacerait
 * le node tout juste ajouté.
 */
export function operationsOf(d: GradeDiff): CopyOp[] {
  const ops: CopyOp[] = d.add.map((node) => ({ kind: 'add', node }));
  for (const c of d.change) {
    for (const node of c.from) ops.push({ kind: 'remove', node });
    ops.push({ kind: 'add', node: c.to });
  }
  for (const node of d.remove) ops.push({ kind: 'remove', node });
  return ops;
}

// ---- Catégories et apparence d'un grade ----

export type NodeKind = 'permission' | 'inheritance' | 'weight' | 'prefix' | 'suffix' | 'meta' | 'displayname';

export const NODE_KINDS: readonly NodeKind[] = ['permission', 'inheritance', 'weight', 'prefix', 'suffix', 'meta', 'displayname'];

/** Catégorie d'un node d'après sa clé, comme LuckPerms les range. */
export function kindOf(key: string): NodeKind {
  const k = key.trimStart().toLowerCase();
  if (k.startsWith('group.')) return 'inheritance';
  if (/^weight\.-?\d+\s*$/.test(k)) return 'weight';
  if (k.startsWith('prefix.')) return 'prefix';
  if (k.startsWith('suffix.')) return 'suffix';
  if (k.startsWith('meta.')) return 'meta';
  if (k.startsWith('displayname.')) return 'displayname';
  return 'permission';
}

function unescapeLp(s: string): string {
  return s.replace(/\\(.)/g, '$1');
}

/** Priorité et texte d'un préfixe ou d'un suffixe (« prefix.100.[Modo] » : 100, « [Modo] »). */
export function chatMetaOf(key: string): { priority: number; text: string } | null {
  const m = /^(?:prefix|suffix)\.(-?\d+)\.([\s\S]*)$/i.exec(key.trimStart());
  return m ? { priority: Number.parseInt(m[1]!, 10), text: unescapeLp(m[2]!) } : null;
}

/** Clé et valeur d'une méta (« meta.couleur.rouge »), séparées au premier point non échappé. */
export function metaOf(key: string): { key: string; value: string } | null {
  const body = /^meta\.([\s\S]*)$/i.exec(key.trimStart())?.[1];
  if (body === undefined) return null;
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '\\') {
      i++;
      continue;
    }
    if (body[i] === '.') return { key: unescapeLp(body.slice(0, i)), value: unescapeLp(body.slice(i + 1)) };
  }
  return null;
}

/** Ce que LuckPerms montre d'un grade (nodes actifs à vrai), pour dire ce qui change. */
export interface GradeLook {
  /** Le plus grand `weight.N`, comme LuckPerms (et weightFromNodes). */
  weight: number | null;
  /** Préfixe et suffixe de plus haute priorité. */
  prefix: string | null;
  suffix: string | null;
  displayName: string | null;
  /** Méta par clé, la première lue pour une clé. */
  meta: Map<string, string>;
  /** Grades hérités (minuscules, triés). */
  inherits: string[];
}

export function lookOf(nodes: readonly GroupNodeDTO[], nowSec: number): GradeLook {
  let weight: number | null = null;
  let prefix: { priority: number; text: string } | null = null;
  let suffix: { priority: number; text: string } | null = null;
  let displayName: string | null = null;
  const meta = new Map<string, string>();
  const inherits = new Set<string>();
  for (const n of nodes) {
    if (!n.value || !isLive(n, nowSec)) continue;
    const key = n.key.trimStart();
    switch (kindOf(key)) {
      case 'weight': {
        const w = Number.parseInt(key.slice('weight.'.length), 10);
        if (weight === null || w > weight) weight = w;
        break;
      }
      case 'prefix': {
        const p = chatMetaOf(key);
        if (p && (!prefix || p.priority > prefix.priority)) prefix = p;
        break;
      }
      case 'suffix': {
        const s = chatMetaOf(key);
        if (s && (!suffix || s.priority > suffix.priority)) suffix = s;
        break;
      }
      case 'displayname':
        if (displayName === null) displayName = key.slice('displayname.'.length);
        break;
      case 'meta': {
        const m = metaOf(key);
        if (m && !meta.has(m.key)) meta.set(m.key, m.value);
        break;
      }
      case 'inheritance':
        inherits.add(key.slice('group.'.length).trim().toLowerCase());
        break;
      default:
        break;
    }
  }
  return {
    weight,
    prefix: prefix?.text ?? null,
    suffix: suffix?.text ?? null,
    displayName,
    meta,
    inherits: [...inherits].sort(),
  };
}

/** Nodes ajoutés, retirés et remplacés (autre valeur, contexte ou fin) d'une catégorie. */
export interface KindCounts {
  added: number;
  removed: number;
  changed: number;
}

export interface GradeChanges {
  counts: Record<NodeKind, KindCounts>;
  /** Le grade dans la cible avant la copie (vide pour un grade à créer) et après (la source). */
  before: GradeLook;
  after: GradeLook;
}

function emptyCounts(): Record<NodeKind, KindCounts> {
  const out = {} as Record<NodeKind, KindCounts>;
  for (const k of NODE_KINDS) out[k] = { added: 0, removed: 0, changed: 0 };
  return out;
}

export function changesOf(diff: GradeDiff, source: readonly GroupNodeDTO[], target: readonly GroupNodeDTO[], nowSec: number): GradeChanges {
  const counts = emptyCounts();
  for (const n of diff.add) counts[kindOf(n.key)].added++;
  for (const c of diff.change) counts[kindOf(c.to.key)].changed++;
  for (const n of diff.remove) counts[kindOf(n.key)].removed++;
  return { counts, before: lookOf(target, nowSec), after: lookOf(source, nowSec) };
}

function total(c: KindCounts): number {
  return c.added + c.removed + c.changed;
}

/** Somme des compteurs de plusieurs grades. */
export function addCounts(a: Record<NodeKind, KindCounts>, b: Record<NodeKind, KindCounts>): Record<NodeKind, KindCounts> {
  const out = emptyCounts();
  for (const k of NODE_KINDS) {
    out[k] = { added: a[k].added + b[k].added, removed: a[k].removed + b[k].removed, changed: a[k].changed + b[k].changed };
  }
  return out;
}

// ---- Textes de l'aperçu ----

/** « 1 permission », « 3 permissions ». */
export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** « 3 ajoutées, 1 retirée, 1 modifiée » ; `feminine` accorde avec le nom (permission, méta). */
export function countsText(c: KindCounts, feminine: boolean): string {
  const e = feminine ? 'e' : '';
  const parts: string[] = [];
  if (c.added) parts.push(`${c.added} ajouté${e}${c.added > 1 ? 's' : ''}`);
  if (c.removed) parts.push(`${c.removed} retiré${e}${c.removed > 1 ? 's' : ''}`);
  if (c.changed) parts.push(`${c.changed} modifié${e}${c.changed > 1 ? 's' : ''}`);
  return parts.join(', ');
}

/**
 * Texte entre guillemets, espaces de bord rendus visibles (␣) : « §3[Modo] » et « §3[Modo]␣ » sont
 * deux préfixes différents, le second laisse un blanc avant le pseudo en jeu.
 */
export function quoted(v: string | null): string {
  if (v === null) return 'aucun';
  return `« ${v.replace(/^\s+|\s+$/g, (m) => '␣'.repeat(m.length))} »`;
}

function listText(xs: readonly string[]): string {
  return xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;
}

const KIND_LABEL: Record<NodeKind, string> = {
  permission: 'Permissions',
  inheritance: 'Héritages',
  weight: 'Poids',
  prefix: 'Préfixe',
  suffix: 'Suffixe',
  meta: 'Métas',
  displayname: 'Nom affiché',
};

/**
 * Lignes « ce qui change » d'un grade à mettre à jour : nombre de permissions ajoutées, retirées,
 * modifiées, puis héritages, poids, préfixe, suffixe, nom affiché et métas qui changent (avant puis
 * après). Une catégorie dont les nodes changent sans que l'effet change (préfixe de priorité plus
 * basse, contexte d'un héritage) le dit.
 */
export function describeUpdate(ch: GradeChanges): string[] {
  const lines: string[] = [];
  const { counts, before, after } = ch;
  if (total(counts.permission)) lines.push(`Permissions : ${countsText(counts.permission, true)}`);

  if (total(counts.inheritance)) {
    const added = after.inherits.filter((g) => !before.inherits.includes(g));
    const removed = before.inherits.filter((g) => !after.inherits.includes(g));
    const parts: string[] = [];
    if (added.length) parts.push(`ajoute ${listText(added)}`);
    if (removed.length) parts.push(`retire ${listText(removed)}`);
    lines.push(parts.length
      ? `Héritages : ${parts.join(', ')}`
      : `Héritages : ${countsText(counts.inheritance, false)} (contexte ou fin)`);
  }

  const effect = (kind: NodeKind, from: string, to: string) => {
    if (!total(counts[kind])) return;
    lines.push(from !== to
      ? `${KIND_LABEL[kind]} : ${from} → ${to}`
      : `${KIND_LABEL[kind]} : ${plural(total(counts[kind]), 'node change', 'nodes changent')}, l'affichage reste ${from}`);
  };
  effect('weight', before.weight === null ? 'aucun' : String(before.weight), after.weight === null ? 'aucun' : String(after.weight));
  effect('prefix', quoted(before.prefix), quoted(after.prefix));
  effect('suffix', quoted(before.suffix), quoted(after.suffix));
  effect('displayname', quoted(before.displayName), quoted(after.displayName));

  if (total(counts.meta)) {
    const added = [...after.meta.keys()].filter((k) => !before.meta.has(k)).sort();
    const removed = [...before.meta.keys()].filter((k) => !after.meta.has(k)).sort();
    const changed = [...after.meta.keys()].filter((k) => before.meta.has(k) && before.meta.get(k) !== after.meta.get(k)).sort();
    const parts: string[] = [];
    if (added.length) parts.push(`ajoute ${listText(added)}`);
    if (changed.length) parts.push(`change ${listText(changed)}`);
    if (removed.length) parts.push(`retire ${listText(removed)}`);
    lines.push(parts.length ? `Métas : ${parts.join(', ')}` : `Métas : ${countsText(counts.meta, true)} (contexte ou fin)`);
  }
  return lines;
}

/** Résumé d'une définition à créer : « 12 permissions », « hérite de modo », « poids 6 »... */
export function describeDefinition(nodes: readonly GroupNodeDTO[], nowSec: number): string[] {
  const look = lookOf(nodes, nowSec);
  const permissions = nodes.filter((n) => isLive(n, nowSec) && kindOf(n.key) === 'permission').length;
  const parts = [plural(permissions, 'permission', 'permissions')];
  if (look.inherits.length) parts.push(`hérite de ${listText(look.inherits)}`);
  if (look.weight !== null) parts.push(`poids ${look.weight}`);
  if (look.prefix !== null) parts.push(`préfixe ${quoted(look.prefix)}`);
  if (look.suffix !== null) parts.push(`suffixe ${quoted(look.suffix)}`);
  if (look.displayName !== null) parts.push(`nom affiché ${quoted(look.displayName)}`);
  if (look.meta.size) parts.push(plural(look.meta.size, 'méta', 'métas'));
  return parts;
}

// ---- Gardes (miroir de la passerelle et du satellite) ----

const CHAT_META_PREFIXES = ['prefix.', 'suffix.', 'meta.', 'displayname.'];

function isChatMeta(lowerNode: string): boolean {
  return CHAT_META_PREFIXES.some((p) => lowerNode.startsWith(p));
}

function withoutSpaces(s: string): string {
  // Espaces et séparateurs Unicode, insécables compris : « ad min » ne contourne pas « admin ».
  return s.replace(/[\s\p{Z}]+/gu, '');
}

/** `admin`, un joker, ou un grade de la liste des grades protégés. */
function isProtectedName(grade: string, protectedGrades: readonly string[]): boolean {
  if (!grade) return false;
  if (grade === 'admin' || grade.includes('*')) return true;
  return protectedGrades.some((p) => withoutSpaces(p.toLowerCase()) === grade);
}

/**
 * Grade que la cible refuse d'éditer à un non-admin global : `admin` ou un grade protégé (même règle
 * que CallerContext.cannotEditGrade du satellite).
 */
export function isProtectedTarget(name: string, protectedGrades: readonly string[]): boolean {
  const g = name.trim().toLowerCase();
  return g === 'admin' || protectedGrades.some((p) => p.trim().toLowerCase() === g);
}

/**
 * Motif du refus d'un node qu'un non-admin global écrit dans un grade, ou null s'il est permis.
 * Miroir de RankNodeGuard (passerelle et satellite) : joker total, nodes regex, administration de
 * LuckPerms, notation abrégée « (a|b) » et guillemets hors nodes de chat, héritage ou droit
 * d'attribuer un grade protégé. Le serveur reste juge : ce test évite seulement de lancer une copie
 * refusée à mi-chemin.
 */
export function copyNodeRefusal(raw: string, protectedGrades: readonly string[]): string | null {
  const node = raw.trim().toLowerCase();
  if (!node) return null;
  const shown = raw.trim();
  if (node === '*' || node.startsWith('r=') || node === 'luckperms' || node.startsWith('luckperms.')
    || (!isChatMeta(node) && /[()'"]/.test(node))) {
    return `Le node « ${shown} » donne la main sur LuckPerms : réservé aux admins du panel.`;
  }
  const after = (prefix: string) => (node.startsWith(prefix) ? withoutSpaces(node.slice(prefix.length)) : null);
  const inherited = after('group.');
  if (inherited !== null && isProtectedName(inherited, protectedGrades)) {
    return `Hériter du grade protégé « ${inherited} » est réservé aux admins du panel.`;
  }
  const grantable = after('recube.allow-rank.');
  if (grantable !== null && isProtectedName(grantable, protectedGrades)) {
    return `Le droit d'attribuer le grade protégé « ${grantable} » est réservé aux admins du panel.`;
  }
  return null;
}

// ---- Plan de la copie (aperçu) ----

export type GradeCopyStatus = 'create' | 'update' | 'same' | 'targetOnly';

/** Un grade lu dans un groupe. */
export interface GradeSide {
  name: string;
  weight?: number | null;
  /** Nodes lus ; null : non lus (grade seulement dans la cible) ou lecture en échec (`error`). */
  nodes: GroupNodeDTO[] | null;
  error?: string;
}

export interface GradeCopyEntry {
  /** Nom du grade (celui de la source, sinon de la cible). */
  name: string;
  status: GradeCopyStatus;
  /** Poids de la source ; de la cible pour un grade qui n'existe que là. */
  weight: number | null;
  /** Définition à copier : nodes de la source lus pour l'aperçu. */
  source: GroupNodeDTO[];
  /** Nodes de la cible lus pour l'aperçu (vide pour un grade à créer). */
  target: GroupNodeDTO[];
  diff: GradeDiff;
  changes: GradeChanges;
  /** Écritures prévues, création comprise. */
  operations: number;
  /** Ce qui empêche de copier ce grade : lecture en échec, grade protégé, node réservé aux admins. */
  blocked?: string;
}

export interface GradeCopyPlanInput {
  source: GradeSide[];
  target: GradeSide[];
  /** Grades protégés, lus dans la réponse du groupe cible. */
  protectedGrades: readonly string[];
  /** Admin global du panel : ni grade protégé, ni node réservé (comme la passerelle et le satellite). */
  isAdmin: boolean;
  sourceLabel: string;
  targetLabel: string;
  nowSec: number;
}

const STATUS_ORDER: Record<GradeCopyStatus, number> = { create: 0, update: 1, same: 2, targetOnly: 3 };

/**
 * Grade par grade, ce que la copie ferait : à créer, à mettre à jour (avec ce qui change), identique,
 * ou seulement dans la cible (jamais touché). Les noms se comparent sans la casse, comme LuckPerms.
 * Tri : statut, puis du plus lourd au plus léger, puis le nom.
 */
export function planGradeCopy(i: GradeCopyPlanInput): GradeCopyEntry[] {
  const targets = new Map(i.target.map((t) => [t.name.toLowerCase(), t]));
  const sourceNames = new Set(i.source.map((s) => s.name.toLowerCase()));
  const entries: GradeCopyEntry[] = [];
  // Poids lu dans les nodes quand ils sont là (même règle que LuckPerms), sinon celui de la liste :
  // la liste peut en manquer un (lecture en parallèle côté satellite), les nodes non.
  const weightOf = (side: GradeSide | undefined): number | null => {
    if (!side) return null;
    return (side.nodes ? lookOf(side.nodes, i.nowSec).weight : null) ?? side.weight ?? null;
  };

  for (const s of i.source) {
    const t = targets.get(s.name.toLowerCase());
    const status: GradeCopyStatus = !t ? 'create' : 'update';
    const sourceNodes = s.nodes ?? [];
    const targetNodes = t?.nodes ?? [];
    const diff = diffNodes(sourceNodes, targetNodes, i.nowSec);
    let blocked: string | undefined;
    if (s.nodes === null) blocked = `Lecture sur ${i.sourceLabel} impossible : ${trimDot(s.error ?? 'erreur inconnue')}.`;
    else if (t && t.nodes === null) blocked = `Lecture sur ${i.targetLabel} impossible : ${trimDot(t.error ?? 'erreur inconnue')}.`;
    const finalStatus: GradeCopyStatus = !blocked && status === 'update' && diffSize(diff) === 0 ? 'same' : status;
    if (!blocked && finalStatus !== 'same' && !i.isAdmin) {
      if (isProtectedTarget(s.name, i.protectedGrades)) {
        blocked = `Grade protégé : sa copie est réservée aux admins du panel.`;
      } else {
        for (const n of [...diff.add, ...diff.change.map((c) => c.to)]) {
          const refused = copyNodeRefusal(n.key, i.protectedGrades);
          if (refused) {
            blocked = refused;
            break;
          }
        }
      }
    }
    entries.push({
      name: s.name,
      status: finalStatus,
      weight: weightOf(s) ?? weightOf(t),
      source: sourceNodes,
      target: targetNodes,
      diff,
      changes: changesOf(diff, sourceNodes, targetNodes, i.nowSec),
      operations: diffSize(diff) + (status === 'create' ? 1 : 0) + diff.change.reduce((a, c) => a + c.from.length, 0),
      ...(blocked ? { blocked } : {}),
    });
  }

  for (const t of i.target) {
    if (sourceNames.has(t.name.toLowerCase())) continue;
    const diff: GradeDiff = { add: [], change: [], remove: [] };
    entries.push({
      name: t.name,
      status: 'targetOnly',
      weight: weightOf(t),
      source: [],
      target: t.nodes ?? [],
      diff,
      changes: changesOf(diff, [], [], i.nowSec),
      operations: 0,
    });
  }

  return entries.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    || (b.weight ?? 0) - (a.weight ?? 0)
    || a.name.localeCompare(b.name));
}

/** Un grade qu'on peut cocher : à créer ou à mettre à jour, sans blocage. */
export function isSelectable(e: GradeCopyEntry): boolean {
  return (e.status === 'create' || e.status === 'update') && !e.blocked;
}

/** Cochés à l'ouverture : tous ceux qu'on peut copier. */
export function defaultSelection(entries: readonly GradeCopyEntry[]): Set<string> {
  return new Set(entries.filter(isSelectable).map((e) => e.name));
}

/**
 * Héritages d'un grade coché vers un grade qui n'existera pas dans la cible : absent de la cible et
 * pas coché pour y être créé. LuckPerms garderait un héritage vers un grade inexistant.
 */
export function danglingInheritances(entry: GradeCopyEntry, entries: readonly GradeCopyEntry[], selected: ReadonlySet<string>, nowSec: number): string[] {
  const existing = new Set(entries.filter((e) => e.status !== 'create').map((e) => e.name.toLowerCase()));
  for (const e of entries) {
    if (e.status === 'create' && selected.has(e.name)) existing.add(e.name.toLowerCase());
  }
  return lookOf(entry.source, nowSec).inherits.filter((g) => !existing.has(g));
}

/** Récapitulatif de la confirmation. */
export interface CopySelectionSummary {
  create: string[];
  update: string[];
  counts: Record<NodeKind, KindCounts>;
  /** Écritures prévues. */
  operations: number;
  /** Grades qui n'existent que dans la cible : jamais touchés. */
  targetOnly: number;
  /** Grades cochables laissés de côté. */
  unchecked: number;
}

export function summarizeSelection(entries: readonly GradeCopyEntry[], selected: ReadonlySet<string>): CopySelectionSummary {
  const chosen = entries.filter((e) => isSelectable(e) && selected.has(e.name));
  return {
    create: chosen.filter((e) => e.status === 'create').map((e) => e.name),
    update: chosen.filter((e) => e.status === 'update').map((e) => e.name),
    counts: chosen.reduce((acc, e) => addCounts(acc, e.changes.counts), emptyCounts()),
    operations: chosen.reduce((a, e) => a + e.operations, 0),
    targetOnly: entries.filter((e) => e.status === 'targetOnly').length,
    unchecked: entries.filter((e) => isSelectable(e) && !selected.has(e.name)).length,
  };
}

/** Phrases du récapitulatif, par catégorie : « 42 permissions ajoutées, 7 retirées », « 3 héritages changent »... */
export function describeTotals(counts: Record<NodeKind, KindCounts>): string[] {
  const lines: string[] = [];
  if (total(counts.permission)) lines.push(`Permissions : ${countsText(counts.permission, true)}`);
  const others: string[] = [];
  const label: Partial<Record<NodeKind, [string, string]>> = {
    inheritance: ['héritage', 'héritages'], weight: ['poids', 'poids'], prefix: ['préfixe', 'préfixes'],
    suffix: ['suffixe', 'suffixes'], meta: ['méta', 'métas'], displayname: ['nom affiché', 'noms affichés'],
  };
  for (const k of NODE_KINDS) {
    const l = label[k];
    if (l && total(counts[k])) others.push(plural(total(counts[k]), l[0], l[1]));
  }
  if (others.length) lines.push(`Nodes qui changent : ${others.join(', ')}`);
  return lines;
}

/**
 * Groupe cible proposé : le premier qui n'est pas « grades seuls » (lobby), sinon le premier. Iris
 * propose ainsi Nova, et Nova propose Iris.
 */
export function defaultCopyTarget<T extends { key: string }>(candidates: readonly T[], ranksOnly: (g: T) => boolean): T | null {
  return candidates.find((g) => !ranksOnly(g)) ?? candidates[0] ?? null;
}

// ---- Lecture avec un nombre limité d'appels en même temps ----

export async function mapLimit<T, R>(items: readonly T[], limit: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]!, i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(Math.max(1, limit), items.length) }, worker));
  return out;
}

// ---- Exécution ----

/** Appels de la copie, tous vers le groupe CIBLE (requêtes à groupe imposé). */
export interface GradeCopyApi {
  createGrade(name: string): Promise<{ error?: string }>;
  readNodes(name: string): Promise<{ data?: { permissions: GroupNodeDTO[]; detailed?: boolean }; error?: string }>;
  addNode(name: string, node: GroupNodeDTO): Promise<{ error?: string }>;
  removeNode(name: string, node: GroupNodeDTO): Promise<{ error?: string }>;
}

export interface GradeCopyResult {
  name: string;
  ok: boolean;
  /** Grade créé dans la cible. */
  created: boolean;
  /** Écritures faites sur `total` (création non comptée). */
  applied: number;
  total: number;
  /** Motif d'un échec. */
  error?: string;
  /** Écritures faites, mais la relecture n'a pas pu le confirmer. */
  unverified?: string;
}

export interface GradeCopyProgress {
  /** Étapes faites sur `total` : une par création, une par grade copié (au prorata de ses écritures). */
  done: number;
  total: number;
  /** Grade en cours, null à la fin. */
  current: string | null;
  /** Écritures du grade en cours. */
  currentApplied: number;
  currentTotal: number;
  results: GradeCopyResult[];
}

function trimDot(s: string): string {
  return s.replace(/[.\s]+$/, '');
}

/** Motif lisible d'un refus renvoyé par le service API (ses deux messages génériques sont en anglais). */
export function explainError(error: string | undefined): string {
  if (!error) return 'erreur inconnue';
  if (error === 'Permission denied') return "refusé par le panel : ton grade ne permet pas d'écrire ce grade sur ce groupe";
  if (error === 'Not authenticated') return 'session expirée, reconnecte-toi';
  return trimDot(error);
}

/** « ajout de « essentials.fly » (server=nova-1) », pour situer une écriture en échec. */
export function describeOp(op: CopyOp): string {
  const ctx = normalizeContext(op.node.context).map((c) => `${c.key}=${c.value}`);
  const extra = [...ctx, ...(expiryOf(op.node) !== null ? ['temporaire'] : [])];
  return `${op.kind === 'add' ? 'ajout' : 'retrait'} de « ${op.node.key} »${op.node.value ? '' : ' (false)'}${extra.length ? ` (${extra.join(', ')})` : ''}`;
}

/**
 * Copie les grades cochés dans la cible, sans jamais s'arrêter sur un échec : d'abord la création des
 * grades absents (un héritage vers un grade copié trouve ainsi son grade), puis, grade par grade,
 * lecture fraîche de la cible, écritures de la différence (la cible a pu bouger depuis l'aperçu ; la
 * définition copiée reste celle de l'aperçu), et relecture : le grade n'est réussi que s'il est
 * identique à la source. Un grade s'arrête à sa première écriture refusée, les suivants continuent.
 */
export async function runGradeCopy(
  entries: readonly GradeCopyEntry[],
  api: GradeCopyApi,
  opts: { targetLabel: string; now: () => number; onProgress?: (p: GradeCopyProgress) => void },
): Promise<GradeCopyResult[]> {
  const chosen = entries.filter(isSelectable);
  const creations = chosen.filter((e) => e.status === 'create');
  const results = new Map<string, GradeCopyResult>(chosen.map((e) => [e.name, { name: e.name, ok: true, created: false, applied: 0, total: 0 }]));
  // Grades terminés, dans l'ordre où ils le sont : le compte rendu se remplit au fil de la copie.
  const finished: GradeCopyResult[] = [];
  const steps = creations.length + chosen.length;
  let done = 0;
  const report = (current: string | null, currentApplied = 0, currentTotal = 0) => {
    opts.onProgress?.({
      done: done + (currentTotal > 0 ? currentApplied / currentTotal : 0),
      total: steps,
      current,
      currentApplied,
      currentTotal,
      results: finished.map((r) => ({ ...r })),
    });
  };
  const finish = (r: GradeCopyResult, error?: string) => {
    if (error) {
      r.ok = false;
      r.error = error;
    }
    finished.push(r);
    done++;
  };

  for (const e of creations) {
    report(e.name);
    const res = await api.createGrade(e.name);
    const r = results.get(e.name)!;
    if (res.error) {
      // Pas de copie pour lui : son étape de copie compte comme faite.
      finish(r, `création impossible : ${explainError(res.error)}`);
      done++;
    } else {
      r.created = true;
      done++;
    }
  }

  for (const e of chosen) {
    const r = results.get(e.name)!;
    if (!r.ok) continue;
    report(e.name);
    const fresh = await api.readNodes(e.name);
    if (!fresh.data) {
      finish(r, `lecture du grade sur ${opts.targetLabel} impossible : ${explainError(fresh.error)}`);
      continue;
    }
    if (!fresh.data.detailed) {
      finish(r, `le satellite de ${opts.targetLabel} ne donne pas les contextes des permissions : rien n'est écrit`);
      continue;
    }
    const ops = operationsOf(diffNodes(e.source, fresh.data.permissions, opts.now()));
    r.total = ops.length;
    let refused: string | undefined;
    for (let k = 0; k < ops.length; k++) {
      const op = ops[k]!;
      report(e.name, k, ops.length);
      const res = op.kind === 'add' ? await api.addNode(e.name, op.node) : await api.removeNode(e.name, op.node);
      if (res.error) {
        refused = `écriture ${k + 1} sur ${ops.length} refusée (${describeOp(op)}) : ${explainError(res.error)}`;
        break;
      }
      r.applied++;
    }
    if (refused) {
      finish(r, refused);
      continue;
    }
    report(e.name, ops.length, ops.length);
    const check = await api.readNodes(e.name);
    if (!check.data) {
      r.unverified = `relecture impossible (${explainError(check.error)})`;
      finish(r);
      continue;
    }
    const left = diffSize(diffNodes(e.source, check.data.permissions, opts.now()));
    finish(r, left > 0 ? `après la copie, ${plural(left, 'différence reste', 'différences restent')} avec la source` : undefined);
  }

  report(null);
  return chosen.map((e) => results.get(e.name)!);
}

/** Bilan de la fin : « 8 grades copiés sur Nova. », avec les échecs s'il y en a. */
export function copyOutcome(results: readonly GradeCopyResult[], targetLabel: string): { level: 'success' | 'warning' | 'error'; message: string } {
  const ok = results.filter((r) => r.ok).length;
  const failed = results.length - ok;
  if (failed === 0) return { level: 'success', message: `${plural(ok, 'grade copié', 'grades copiés')} sur ${targetLabel}.` };
  if (ok === 0) return { level: 'error', message: `Aucun grade copié sur ${targetLabel} : ${plural(failed, 'échec', 'échecs')}.` };
  return { level: 'warning', message: `${plural(ok, 'grade copié', 'grades copiés')} sur ${targetLabel}, ${plural(failed, 'en échec', 'en échec')}.` };
}
